import React from 'react'
import { FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaTimes, FaSpinner } from 'react-icons/fa'
import { PIPELINE, STATUS } from '../lib/viz'
import { statusLabel } from '../lib/format'

/**
 * Small shared pieces. Kept in one file because each is a handful of lines and
 * ten one-component files is harder to navigate than one index.
 */

// ---------------------------------------------------------------------------
//  Card
// ---------------------------------------------------------------------------
export const Card = ({ title, subtitle, action, children, className = '', bodyClassName = 'p-4' }) => (
  <section className={`card ${className}`}>
    {(title || action) && (
      <div className="card-header">
        <div className="min-w-0">
          {title && <h2 className="card-title">{title}</h2>}
          {subtitle && <p className="card-subtitle">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </section>
)

// ---------------------------------------------------------------------------
//  Status badge
// ---------------------------------------------------------------------------
/**
 * Order status as a tinted chip.
 *
 * The chip carries the status *word*, always. The colour is a fast secondary cue
 * for someone scanning a long list, never the only way to tell a cancelled order
 * from a delivered one.
 *
 * Text sits on a 12%-alpha wash of the status hue rather than the hue itself:
 * several of these colours are below 3:1 against white, and white-on-warning is
 * genuinely unreadable.
 */
export const StatusBadge = ({ status }) => {
  const color = PIPELINE[status] ?? STATUS.serious
  return (
    <span className="badge" style={{ backgroundColor: `${color}1f`, color: shade(color) }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} aria-hidden="true" />
      {statusLabel(status)}
    </span>
  )
}

/**
 * Darkens a hex colour so label text clears 4.5:1 on its own pale wash.
 *
 * The ramp's lighter steps (pending #86b6ef) have nowhere near enough contrast to
 * be used as text. Mixing toward black by a fixed ratio keeps the hue
 * recognisable while making the word readable.
 */
function shade(hex, amount = 0.55) {
  const n = parseInt(hex.slice(1), 16)
  const r = Math.round(((n >> 16) & 255) * (1 - amount))
  const g = Math.round(((n >> 8) & 255) * (1 - amount))
  const b = Math.round((n & 255) * (1 - amount))
  return `rgb(${r}, ${g}, ${b})`
}

export const StockBadge = ({ available, threshold = 3 }) => {
  const n = Number(available) || 0
  const tone = n === 0 ? 'critical' : n <= threshold ? 'warning' : 'good'
  const color = STATUS[tone === 'good' ? 'good' : tone]
  const label = n === 0 ? 'Out of stock' : n <= threshold ? `Low · ${n}` : `${n} in stock`
  return (
    <span className="badge" style={{ backgroundColor: `${color}1f`, color: shade(color, 0.45) }}>
      {tone !== 'good' && <FaExclamationTriangle className="text-[8px]" aria-hidden="true" />}
      {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
//  Feedback
// ---------------------------------------------------------------------------
export const Banner = ({ tone = 'info', children, onDismiss }) => {
  const config = {
    info: { color: '#2a78d6', Icon: FaInfoCircle },
    success: { color: STATUS.good, Icon: FaCheckCircle },
    warning: { color: STATUS.warning, Icon: FaExclamationTriangle },
    error: { color: STATUS.critical, Icon: FaExclamationTriangle },
  }[tone]

  return (
    <div
      className="flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13px]"
      style={{ borderColor: `${config.color}40`, backgroundColor: `${config.color}0f`, color: shade(config.color, 0.4) }}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <config.Icon className="mt-0.5 shrink-0 text-[12px]" style={{ color: config.color }} aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="shrink-0 opacity-55 hover:opacity-100" aria-label="Dismiss">
          <FaTimes className="text-[11px]" />
        </button>
      )}
    </div>
  )
}

export const Spinner = ({ label = 'Loading…' }) => (
  <div className="flex items-center justify-center gap-2.5 py-12 text-sm text-ink-muted">
    <FaSpinner className="animate-spin" aria-hidden="true" />
    {label}
  </div>
)

export const EmptyState = ({ icon: Icon, title, children, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    {Icon && <Icon className="mb-3 text-2xl text-ink-muted/45" aria-hidden="true" />}
    <h3 className="text-sm font-semibold text-ink">{title}</h3>
    {children && <p className="mt-1 max-w-sm text-2xs text-ink-muted">{children}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
)

// ---------------------------------------------------------------------------
//  Page header
// ---------------------------------------------------------------------------
export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
    <div className="min-w-0">
      <h1 className="font-heading text-xl font-bold tracking-tight text-ink">{title}</h1>
      {subtitle && <p className="mt-0.5 text-2xs text-ink-muted">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
)

// ---------------------------------------------------------------------------
//  Modal
// ---------------------------------------------------------------------------
export const Modal = ({ open, onClose, title, children, footer, wide = false }) => {
  // Escape closes it. A dialog that can only be dismissed by finding the X is a
  // trap for anyone working from the keyboard.
  React.useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <button className="fixed inset-0 bg-black/45" onClick={onClose} aria-label="Close" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 w-full animate-scale-in rounded-xl border border-hairline bg-surface shadow-pop ${
          wide ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        <div className="card-header">
          <h2 className="card-title">{title}</h2>
          <button onClick={onClose} className="btn-subtle btn-sm" aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-hairline px-4 py-3">{footer}</div>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Toast
// ---------------------------------------------------------------------------
export const Toast = ({ toast, onDismiss }) => {
  React.useEffect(() => {
    if (!toast) return
    // Errors stay until dismissed — an auto-hiding error is an error nobody read.
    if (toast.tone === 'error') return
    const timer = setTimeout(onDismiss, 4000)
    return () => clearTimeout(timer)
  }, [toast, onDismiss])

  if (!toast) return null

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-full max-w-sm animate-slide-up">
      <Banner tone={toast.tone} onDismiss={onDismiss}>
        {toast.message}
      </Banner>
    </div>
  )
}

/** Toast state as a hook, so pages do not each re-implement it. */
export function useToast() {
  const [toast, setToast] = React.useState(null)
  return {
    toast,
    dismiss: () => setToast(null),
    notify: (message, tone = 'success') => setToast({ message, tone }),
    notifyError: (err) => setToast({ message: err?.message ?? String(err), tone: 'error' }),
  }
}

export { shade }
